import React, { FC, useState } from "react";
import { Alert, Button, Card, Col, Row, Spinner } from "react-bootstrap";

import { AiGoalType, WeeklyUserSummaryType } from "../../../../DTO";
import { AlignmentSection } from "./alignment-section";
import { GoalCard } from "./goal-card";
import { TrafficLight } from "../../../../components";
import { formatDateTime, prettifyKey } from "./summary-helpers";

type WeeklySummarySectionProps = {
    hasWeekSelection: boolean;
    selectedWeekId?: number;
    loading: boolean;
    error: string;
    summary?: WeeklyUserSummaryType;
    onRetry: () => void;
    weekGoal?: AiGoalType | null;
    onGoalUpdated?: (goal: AiGoalType) => void;
    daysWithRecords?: number;
    totalWeekDays?: number;
};

const PRIMARY_NUTRITION_KEYS = ["calories", "protein", "carbohydrates", "fat"];
const SECONDARY_NUTRITION_KEYS = [
    "fiber",
    "sugar",
    "sodium",
    "cholesterol",
    "saturated_fat",
    "trans_fat",
];

const getNutritionUnit = (key: string) => {
    if (key === "calories") return "kcal";
    if (key === "sodium" || key === "cholesterol") return "mg";
    return "g";
};

const formatMetric = (value: any, key: string) => {
    const num = Number(value ?? 0);
    if (Number.isNaN(num)) return String(value ?? "-");
    if (key === "calories") return String(Math.round(num));
    return String(Number(num.toFixed(1)));
};

const toSafePercent = (value: number, max: number) => {
    if (!max) return 0;
    return Math.max(0, Math.min(100, (value / max) * 100));
};

export const WeeklySummarySection: FC<WeeklySummarySectionProps> = ({
    hasWeekSelection,
    selectedWeekId,
    loading,
    error,
    summary,
    onRetry,
    weekGoal,
    onGoalUpdated,
    daysWithRecords = 0,
    totalWeekDays = 7,
}) => {
    const [innerTab, setInnerTab] = useState<
        "overview" | "nutrition" | "insights"
    >("overview");

    const hasSelectedWeek = hasWeekSelection && (selectedWeekId ?? 0) > 0;
    const noDataDays = summary
        ? Math.max(
              0,
              summary.traffic_light_distribution.total_days -
                  (summary.traffic_light_distribution.green_days +
                      summary.traffic_light_distribution.amber_days +
                      summary.traffic_light_distribution.red_days),
          )
        : 0;

    return (
        <Card className="summary-panel summary-panel-content shadow-sm border-0">
            <Card.Body className="p-3 p-md-4">
                <div className="summary-section-head mb-3">
                    <div>
                        <div className="summary-caption">Weekly View</div>
                        <h2 className="h5 mb-0">Weekly Summary</h2>
                    </div>
                </div>

                {!hasSelectedWeek && (
                    <Alert variant="light" className="mb-0">
                        Select a week to load summary.
                    </Alert>
                )}

                {hasSelectedWeek && loading && (
                    <div className="d-flex align-items-center gap-2">
                        <Spinner size="sm" animation="border" />
                        <span className="small text-muted">
                            Loading weekly summary...
                        </span>
                    </div>
                )}

                {hasSelectedWeek && !loading && error && (
                    <Alert variant="danger" className="mb-0">
                        <div className="mb-2">{error}</div>
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={onRetry}
                            aria-label="Retry weekly summary"
                        >
                            Retry
                        </Button>
                    </Alert>
                )}

                {hasSelectedWeek &&
                    !loading &&
                    !error &&
                    summary &&
                    !summary.has_summary && (
                        <div className="summary-stack-sm">
                            <Alert
                                variant="light"
                                className="mb-0 summary-explainer"
                            >
                                <strong>
                                    {daysWithRecords > 0
                                        ? "A scored weekly summary has not been generated yet."
                                        : "There are no recorded health logs for this week."}
                                </strong>
                                <div>
                                    {daysWithRecords > 0
                                        ? `${daysWithRecords} of ${totalWeekDays} days contain records. The traffic-light review below uses the available sleep, activity, meal, and review data; nutrition averages appear after daily summaries are generated.`
                                        : "A weekly score and traffic-light rating cannot be calculated until the user records at least one day."}
                                </div>
                            </Alert>
                            {daysWithRecords > 0 &&
                                summary.alignment_summary && (
                                    <AlignmentSection
                                        title="Available weekly traffic-light review"
                                        alignment={summary.alignment_summary}
                                    />
                                )}
                        </div>
                    )}

                {hasSelectedWeek &&
                    !loading &&
                    !error &&
                    summary?.has_summary && (
                        <div className="d-flex flex-column gap-3">
                            <div
                                className="summary-inner-tabs"
                                role="tablist"
                                aria-label="Weekly content tabs"
                            >
                                {(
                                    [
                                        "overview",
                                        "nutrition",
                                        "insights",
                                    ] as const
                                ).map((tab) => (
                                    <button
                                        key={tab}
                                        type="button"
                                        role="tab"
                                        aria-selected={innerTab === tab}
                                        className={`summary-inner-tab-btn ${innerTab === tab ? "summary-inner-tab-btn-active" : ""}`}
                                        onClick={() => setInnerTab(tab)}
                                    >
                                        {tab.charAt(0).toUpperCase() +
                                            tab.slice(1)}
                                    </button>
                                ))}
                            </div>

                            {innerTab === "overview" && (
                                <>
                                    <div className="summary-headline">
                                        <div>
                                            <div className="summary-title">
                                                {summary.display.emoji}{" "}
                                                {summary.display.title}
                                            </div>
                                            <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
                                                <TrafficLight
                                                    status={
                                                        summary.display.light
                                                    }
                                                    compact
                                                    label="Weekly rating"
                                                />
                                                <span className="small text-muted">
                                                    Generated{" "}
                                                    {formatDateTime(
                                                        summary.generated_at,
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="summary-score text-end">
                                            <div>
                                                {summary.score.percentage}%
                                            </div>
                                            <div className="summary-score-sub">
                                                {summary.score.total}/
                                                {summary.score.max}
                                            </div>
                                        </div>
                                    </div>

                                    {summary.alignment_summary && (
                                        <section
                                            className="weekly-traffic-report"
                                            aria-label="Weekly traffic light report"
                                        >
                                            <div className="weekly-traffic-overall">
                                                <div>
                                                    <div className="summary-tile-label">
                                                        Overall rating
                                                    </div>
                                                    <h3>
                                                        {summary.display.title}
                                                    </h3>
                                                    <p>
                                                        {summary.insights
                                                            .weekly_insight ||
                                                            "Weekly rating based on the five tracked health areas."}
                                                    </p>
                                                </div>
                                                <div className="weekly-traffic-counts">
                                                    <span
                                                        className={`weekly-signal is-${summary.alignment_summary.overall.overall_status}`}
                                                    >
                                                        <i />
                                                        <i />
                                                        <i />
                                                    </span>
                                                    <div>
                                                        <span>
                                                            On track{" "}
                                                            <strong>
                                                                {
                                                                    summary
                                                                        .alignment_summary
                                                                        .overall
                                                                        .traffic_light_counts
                                                                        .green
                                                                }
                                                            </strong>
                                                        </span>
                                                        <span>
                                                            Needs attention{" "}
                                                            <strong>
                                                                {
                                                                    summary
                                                                        .alignment_summary
                                                                        .overall
                                                                        .traffic_light_counts
                                                                        .amber
                                                                }
                                                            </strong>
                                                        </span>
                                                        <span>
                                                            Off track{" "}
                                                            <strong>
                                                                {
                                                                    summary
                                                                        .alignment_summary
                                                                        .overall
                                                                        .traffic_light_counts
                                                                        .red
                                                                }
                                                            </strong>
                                                        </span>
                                                        {summary
                                                            .alignment_summary
                                                            .overall
                                                            .traffic_light_counts
                                                            .no_data > 0 && (
                                                            <span>
                                                                Not enough data{" "}
                                                                <strong>
                                                                    {
                                                                        summary
                                                                            .alignment_summary
                                                                            .overall
                                                                            .traffic_light_counts
                                                                            .no_data
                                                                    }
                                                                </strong>
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="weekly-report-list">
                                                {summary.alignment_summary.cards.map(
                                                    (card) => (
                                                        <article
                                                            key={card.key}
                                                            className={`is-${card.status}`}
                                                        >
                                                            <TrafficLight
                                                                status={
                                                                    card.status
                                                                }
                                                                compact
                                                            />
                                                            <div>
                                                                <strong>
                                                                    {card.title}
                                                                </strong>
                                                                <small>
                                                                    {
                                                                        card.reason
                                                                    }
                                                                </small>
                                                            </div>
                                                            <span className="weekly-report-score">
                                                                {card.status ===
                                                                "no_data"
                                                                    ? "No data"
                                                                    : `${card.score}/100`}
                                                            </span>
                                                        </article>
                                                    ),
                                                )}
                                            </div>
                                        </section>
                                    )}

                                    <div className="summary-kpi-grid">
                                        <div className="summary-kpi-tile">
                                            <div className="summary-tile-label">
                                                Trend
                                            </div>
                                            <div className="summary-kpi-value text-capitalize">
                                                {summary.trend.icon || "→"}{" "}
                                                {summary.trend.direction}
                                            </div>
                                            <div className="summary-kpi-sub">
                                                {summary.trend.score_change}{" "}
                                                score change
                                            </div>
                                        </div>
                                        <div className="summary-kpi-tile">
                                            <div className="summary-tile-label">
                                                Days With Data
                                            </div>
                                            <div className="summary-kpi-value">
                                                {
                                                    summary
                                                        .traffic_light_distribution
                                                        .days_with_data
                                                }
                                            </div>
                                            <div className="summary-kpi-sub">
                                                of{" "}
                                                {
                                                    summary
                                                        .traffic_light_distribution
                                                        .total_days
                                                }
                                            </div>
                                        </div>
                                        <div className="summary-kpi-tile">
                                            <div className="summary-tile-label">
                                                Worst Category
                                            </div>
                                            <div className="summary-kpi-value text-capitalize">
                                                {summary.insights
                                                    .worst_category || "-"}
                                            </div>
                                        </div>
                                        <div className="summary-kpi-tile">
                                            <div className="summary-tile-label">
                                                Traffic Lights
                                            </div>
                                            <div className="summary-kpi-sub">
                                                {summary
                                                    .traffic_light_distribution
                                                    .days_with_data > 0
                                                    ? `${summary.traffic_light_distribution.days_with_data} rated day${summary.traffic_light_distribution.days_with_data === 1 ? "" : "s"}; ${noDataDays} without enough data`
                                                    : "No days have enough data for a rating"}
                                            </div>
                                        </div>
                                    </div>

                                    <Row className="g-3">
                                        <Col xl={7}>
                                            <div className="summary-tile h-100">
                                                <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                                                    <div className="summary-tile-label mb-0">
                                                        Weekly Consistency
                                                    </div>
                                                    <span className="small text-muted">
                                                        {
                                                            summary
                                                                .traffic_light_distribution
                                                                .total_days
                                                        }{" "}
                                                        days
                                                    </span>
                                                </div>
                                                <div className="summary-consistency-grid mt-2">
                                                    <div className="summary-consistency-item">
                                                        <TrafficLight
                                                            status="green"
                                                            compact
                                                            label="On track"
                                                        />
                                                        <strong>
                                                            {
                                                                summary
                                                                    .traffic_light_distribution
                                                                    .green_days
                                                            }
                                                        </strong>
                                                    </div>
                                                    <div className="summary-consistency-item">
                                                        <TrafficLight
                                                            status="amber"
                                                            compact
                                                            label="Needs attention"
                                                        />
                                                        <strong>
                                                            {
                                                                summary
                                                                    .traffic_light_distribution
                                                                    .amber_days
                                                            }
                                                        </strong>
                                                    </div>
                                                    <div className="summary-consistency-item">
                                                        <TrafficLight
                                                            status="red"
                                                            compact
                                                            label="Off track"
                                                        />
                                                        <strong>
                                                            {
                                                                summary
                                                                    .traffic_light_distribution
                                                                    .red_days
                                                            }
                                                        </strong>
                                                    </div>
                                                    <div className="summary-consistency-item">
                                                        <TrafficLight
                                                            status="no_data"
                                                            compact
                                                            label="Not enough data"
                                                        />
                                                        <strong>
                                                            {noDataDays}
                                                        </strong>
                                                    </div>
                                                </div>

                                                <div className="summary-category-grid mt-3">
                                                    {Object.entries(
                                                        summary.category_averages,
                                                    ).map(([key, value]) => (
                                                        <div
                                                            className="summary-category-tile"
                                                            key={key}
                                                        >
                                                            <div className="d-flex justify-content-between align-items-center gap-2">
                                                                <span className="text-capitalize fw-semibold">
                                                                    {key}
                                                                </span>
                                                                <TrafficLight
                                                                    status={
                                                                        value.light
                                                                    }
                                                                    compact
                                                                    label=""
                                                                />
                                                            </div>
                                                            <div className="summary-category-score">
                                                                {formatMetric(
                                                                    value.avg_score,
                                                                    key,
                                                                )}
                                                                <span>/25</span>
                                                            </div>
                                                            <div
                                                                className="summary-mini-bar"
                                                                aria-hidden="true"
                                                            >
                                                                <span
                                                                    style={{
                                                                        width: `${toSafePercent(
                                                                            Number(
                                                                                value.avg_score ||
                                                                                    0,
                                                                            ),
                                                                            25,
                                                                        )}%`,
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </Col>

                                        <Col xl={5}>
                                            <div className="summary-tile h-100">
                                                <div className="summary-tile-label mb-2">
                                                    Coaching Summary
                                                </div>
                                                <div className="summary-stack-sm">
                                                    <div className="summary-mini-block">
                                                        <div className="summary-mini-label">
                                                            Insight
                                                        </div>
                                                        <div className="small">
                                                            {summary.insights
                                                                .weekly_insight ||
                                                                "-"}
                                                        </div>
                                                    </div>

                                                    {summary.insights
                                                        .focus_areas.length >
                                                        0 && (
                                                        <div className="summary-mini-block">
                                                            <div className="summary-mini-label">
                                                                Focus Areas
                                                            </div>
                                                            <div className="d-flex flex-column gap-1">
                                                                {summary.insights.focus_areas.map(
                                                                    (
                                                                        item,
                                                                        index,
                                                                    ) => (
                                                                        <div
                                                                            key={`${item.category}-${index}`}
                                                                            className="summary-focus-item"
                                                                        >
                                                                            <div className="fw-semibold text-capitalize">
                                                                                {
                                                                                    item.category
                                                                                }
                                                                            </div>
                                                                            <div className="small text-muted">
                                                                                {
                                                                                    item.recommendation
                                                                                }
                                                                            </div>
                                                                        </div>
                                                                    ),
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}

                                                    <details className="summary-details">
                                                        <summary className="pointer">
                                                            Trend Description
                                                        </summary>
                                                        <div className="small mt-2 text-muted">
                                                            {summary.trend
                                                                .description ||
                                                                "-"}
                                                        </div>
                                                    </details>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </>
                            )}

                            {innerTab === "nutrition" && (
                                <div className="summary-tile">
                                    <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                                        <div className="summary-tile-label mb-0">
                                            Nutrition Overview
                                        </div>
                                        <span className="small text-muted">
                                            Weekly totals and daily averages
                                        </span>
                                    </div>
                                    <Row className="g-2">
                                        <Col lg={6}>
                                            <div className="summary-subpanel h-100">
                                                <div className="summary-mini-label mb-2">
                                                    Weekly Totals
                                                </div>
                                                <div className="summary-metric-grid">
                                                    {PRIMARY_NUTRITION_KEYS.map(
                                                        (k) => (
                                                            <div
                                                                key={`total-${k}`}
                                                                className="summary-metric-tile"
                                                            >
                                                                <div className="summary-mini-label">
                                                                    {prettifyKey(
                                                                        k,
                                                                    )}
                                                                </div>
                                                                <div className="summary-metric-value">
                                                                    {formatMetric(
                                                                        summary
                                                                            .nutrition
                                                                            .totals?.[
                                                                            k
                                                                        ],
                                                                        k,
                                                                    )}
                                                                </div>
                                                                <div className="summary-metric-unit">
                                                                    {getNutritionUnit(
                                                                        k,
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ),
                                                    )}
                                                </div>
                                            </div>
                                        </Col>
                                        <Col lg={6}>
                                            <div className="summary-subpanel h-100">
                                                <div className="summary-mini-label mb-2">
                                                    Daily Averages
                                                </div>
                                                <div className="summary-metric-grid">
                                                    {PRIMARY_NUTRITION_KEYS.map(
                                                        (k) => (
                                                            <div
                                                                key={`avg-${k}`}
                                                                className="summary-metric-tile"
                                                            >
                                                                <div className="summary-mini-label">
                                                                    {prettifyKey(
                                                                        k,
                                                                    )}
                                                                </div>
                                                                <div className="summary-metric-value">
                                                                    {formatMetric(
                                                                        summary
                                                                            .nutrition
                                                                            .daily_averages?.[
                                                                            k
                                                                        ],
                                                                        k,
                                                                    )}
                                                                </div>
                                                                <div className="summary-metric-unit">
                                                                    {getNutritionUnit(
                                                                        k,
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ),
                                                    )}
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                    <div className="summary-subpanel mt-2">
                                        <div className="summary-mini-label mb-2">
                                            More Metrics
                                        </div>
                                        <Row className="g-2">
                                            <Col md={6}>
                                                <div className="summary-mini-label mb-1">
                                                    Weekly Totals
                                                </div>
                                                <div className="d-flex flex-column gap-1">
                                                    {SECONDARY_NUTRITION_KEYS.map(
                                                        (k) => (
                                                            <div
                                                                key={`wt-${k}`}
                                                                className="summary-key-row"
                                                            >
                                                                <span>
                                                                    {prettifyKey(
                                                                        k,
                                                                    )}
                                                                </span>
                                                                <span className="fw-semibold">
                                                                    {formatMetric(
                                                                        summary
                                                                            .nutrition
                                                                            .totals?.[
                                                                            k
                                                                        ],
                                                                        k,
                                                                    )}{" "}
                                                                    {getNutritionUnit(
                                                                        k,
                                                                    )}
                                                                </span>
                                                            </div>
                                                        ),
                                                    )}
                                                </div>
                                            </Col>
                                            <Col md={6}>
                                                <div className="summary-mini-label mb-1">
                                                    Daily Averages
                                                </div>
                                                <div className="d-flex flex-column gap-1">
                                                    {SECONDARY_NUTRITION_KEYS.map(
                                                        (k) => (
                                                            <div
                                                                key={`wa-${k}`}
                                                                className="summary-key-row"
                                                            >
                                                                <span>
                                                                    {prettifyKey(
                                                                        k,
                                                                    )}
                                                                </span>
                                                                <span className="fw-semibold">
                                                                    {formatMetric(
                                                                        summary
                                                                            .nutrition
                                                                            .daily_averages?.[
                                                                            k
                                                                        ],
                                                                        k,
                                                                    )}{" "}
                                                                    {getNutritionUnit(
                                                                        k,
                                                                    )}
                                                                </span>
                                                            </div>
                                                        ),
                                                    )}
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                </div>
                            )}

                            {innerTab === "insights" && (
                                <>
                                    {summary.alignment_summary ? (
                                        <AlignmentSection
                                            title="Alignment Summary"
                                            alignment={
                                                summary.alignment_summary
                                            }
                                        />
                                    ) : (
                                        <Alert variant="light" className="mb-0">
                                            No alignment data available for this
                                            week.
                                        </Alert>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                {hasSelectedWeek && !loading && weekGoal && onGoalUpdated && (
                    <GoalCard goal={weekGoal} onUpdated={onGoalUpdated} />
                )}

                {hasSelectedWeek && !loading && !weekGoal && (
                    <p className="text-muted small mt-3 mb-0">
                        No goal generated for this week yet.
                    </p>
                )}
            </Card.Body>
        </Card>
    );
};
